import { DialogComponent } from '@theme/dialog';

/**
 * Oath — PDP video pop-up.
 *
 * Extends Horizon's native DialogComponent (the modal engine: showModal, backdrop-click
 * close, ESC, body scroll-lock + scroll restore, open/close animation, focus management,
 * dialog:open / dialog:close events). This class adds ONLY the genuine gap that the native
 * component does not cover: playing the portrait video muted on open, pausing it on close,
 * and a mute/unmute toggle (per the Figma annotation on node 1240:16250 — "auto-played and
 * muted; when the icon is clicked, the audio will play").
 *
 * Decision Ladder Rung 2/3 (native engine reused) — supersedes the S67 Rung-5 custom
 * <oath-video-popup> that hand-rolled the entire dialog lifecycle.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLVideoElement} [video]
 * @property {HTMLButtonElement} [volume]
 *
 * @extends DialogComponent<Refs>
 */
class OathVideoDialog extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:open', this.#handleOpen);
    this.addEventListener('dialog:close', this.#handleClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('dialog:close', this.#handleClose);
  }

  /** On open: reset to muted, play from the start. */
  #handleOpen = () => {
    const video = this.refs.video;
    if (!video) return;
    video.muted = true;
    this.#reflectMuted(true);
    try {
      video.currentTime = 0;
    } catch (error) {
      /* currentTime not yet settable — ignore */
    }
    const played = video.play();
    if (played && played.catch) played.catch(() => {});
  };

  /** On close: pause so audio does not keep playing behind the closed dialog. */
  #handleClose = () => {
    const video = this.refs.video;
    if (video && typeof video.pause === 'function') video.pause();
  };

  /** Toggle audio. Bound declaratively via on:click="/toggleMute". */
  toggleMute = () => {
    const video = this.refs.video;
    if (!video) return;
    video.muted = !video.muted;
    this.#reflectMuted(video.muted);
  };

  /**
   * Reflect the muted state in the toggle button. While muted we show the "sound on"
   * glyph (the tap-to-unmute affordance, matching the Figma Volume "Sound on" default);
   * once audio is on we show the slashed glyph (tap-to-mute).
   *
   * @param {boolean} muted
   */
  #reflectMuted(muted) {
    const { volume } = this.refs;
    if (!volume) return;
    volume.setAttribute('aria-pressed', String(!muted));
    volume.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    const high = volume.querySelector('[data-vol="high"]');
    const mute = volume.querySelector('[data-vol="mute"]');
    if (high) high.hidden = !muted;
    if (mute) mute.hidden = muted;
  }
}

if (!customElements.get('oath-video-dialog')) {
  customElements.define('oath-video-dialog', OathVideoDialog);
}
